<template>
  <div class="proposal-reviewer">
    <div class="reviewer-header">
      <button @click="$emit('back')" class="back-btn">
        ← Back to Proposals
      </button>
      
      <div class="proposal-info">
        <h2>👁️ Review Proposal for {{ tenant.name }}</h2>
        <p>Proposal #{{ proposal.id.slice(-8) }} • {{ getStatusLabel(proposal.status) }}</p>
      </div>
    </div>

    <!-- Proposal Summary -->
    <div class="proposal-summary">
      <div class="summary-header">
        <h3>📋 Proposal Overview</h3>
        <div class="confidence-score">
          <span class="confidence-label">Confidence Score:</span>
          <span 
            class="confidence-value" 
            :class="confidenceClass"
          >
            {{ Math.round((proposal.confidence_score || 0) * 100) }}%
          </span>
        </div>
      </div>

      <div class="summary-stats">
        <div class="stat">
          <span class="label">Schedule Period:</span>
          <span class="value">
            {{ formatDate(proposal.schedule_start_date) }} - {{ formatDate(proposal.schedule_end_date) }}
          </span>
        </div>
        
        <div class="stat">
          <span class="label">Total Shifts:</span>
          <span class="value">{{ proposal.shift_count || proposalShifts.length }}</span>
        </div>
        
        <div class="stat">
          <span class="label">Employees:</span>
          <span class="value">{{ proposal.employee_count || uniqueEmployees }}</span>
        </div>
        
        <div class="stat">
          <span class="label">Generation Method:</span>
          <span class="value">{{ getMethodLabel(proposal.generated_by) }}</span>
        </div>
        
        <div class="stat">
          <span class="label">Created:</span>
          <span class="value">{{ formatDateTime(proposal.created_at) }}</span>
        </div>
      </div>

      <div v-if="proposal.summary" class="summary-description">
        <h4>Summary</h4>
        <p>{{ proposal.summary }}</p>
      </div>
    </div>

    <!-- Schedule Preview -->
    <div class="schedule-preview">
      <h3>📅 Schedule Preview</h3>
      
      <!-- Simple calendar view -->
      <div class="calendar-container">
        <div class="calendar-header">
          <h4>{{ calendarWeekRange }}</h4>
          <div class="calendar-nav">
            <button @click="previousWeek" class="nav-btn">‹</button>
            <button @click="nextWeek" class="nav-btn">›</button>
          </div>
        </div>
        
        <div class="calendar-grid">
          <!-- Days header -->
          <div class="day-header" v-for="day in weekDays" :key="day.toISOString()">
            {{ formatDayHeader(day) }}
          </div>
          
          <!-- Shifts for each day -->
          <div 
            v-for="day in weekDays" 
            :key="day.toISOString() + '-shifts'"
            class="day-shifts"
          >
            <div
              v-for="shift in getShiftsForDay(day)"
              :key="shift.id"
              class="shift-block"
              :class="{ 'has-conflict': hasConflict(shift) }"
            >
              <div class="shift-employee">{{ shift.employee_name }}</div>
              <div class="shift-time">{{ shift.start_time }} - {{ shift.end_time }}</div>
              <div class="shift-role">{{ shift.role || 'Worker' }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Conflicts Section -->
    <div v-if="conflicts.length > 0" class="conflicts-section">
      <h3>⚠️ Detected Conflicts ({{ conflicts.length }})</h3>
      
      <div class="conflicts-list">
        <div
          v-for="conflict in conflicts"
          :key="conflict.id || Math.random()"
          class="conflict-item"
        >
          <div class="conflict-icon">⚠️</div>
          <div class="conflict-details">
            <div class="conflict-type">{{ conflict.type || 'Schedule Conflict' }}</div>
            <div class="conflict-message">{{ conflict.message || 'Overlapping shifts detected' }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Rules Applied -->
    <div v-if="rulesApplied.length > 0" class="rules-section">
      <h3>⚙️ Rules Applied ({{ rulesApplied.length }})</h3>
      
      <div class="rules-list">
        <div
          v-for="rule in rulesApplied"
          :key="rule"
          class="rule-item"
        >
          <span class="rule-icon">✓</span>
          <span class="rule-name">{{ formatRuleName(rule) }}</span>
        </div>
      </div>
    </div>

    <!-- Review Actions -->
    <div class="review-actions">
      <div class="review-notes">
        <label for="review-notes">
          📝 Review Notes & Feedback
          <span class="help-text">Your feedback helps the ML model learn your scheduling preferences</span>
        </label>
        <textarea
          id="review-notes"
          v-model="reviewNotes"
          placeholder="Be specific in your feedback for better ML training:
• 'Too many consecutive days for Anna - max should be 5'
• 'Weekend distribution unfair - Johan has 3 weekends, Maria has 1'
• 'Violates 11h rest period between shifts'
• 'Good balance and fair distribution across all employees'"
          rows="6"
        ></textarea>
        <small class="review-hint">
          💡 Tip: Specific feedback helps the ML model understand what makes a good schedule
        </small>
      </div>
      
      <div class="action-buttons">
        <button 
          @click="updateStatus('approved')"
          class="btn btn-approve"
          :disabled="updating"
        >
          ✅ Approve & Deploy
        </button>
        
        <button 
          @click="updateStatus('reviewing')"
          class="btn btn-changes"
          :disabled="updating"
        >
          ✨ Request Changes (AI Will Regenerate)
        </button>
        
        <button 
          @click="handleReject"
          class="btn btn-reject"
          :disabled="updating"
        >
          ❌ Reject Proposal
        </button>
      </div>
    </div>

    <!-- Rejection Modal -->
    <RejectionModal 
      ref="rejectionModal"
      @confirm="handleRejectionConfirmed"
      @cancel="handleRejectionCancelled"
    />

    <!-- Loading overlay -->
    <div v-if="updating || aiSchedulerStore.generation.isGenerating" class="updating-overlay">
      <div class="spinner-container">
        <div class="spinner"></div>
        <div v-if="aiSchedulerStore.generation.isGenerating" class="generation-status">
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: aiSchedulerStore.generation.progress + '%' }"></div>
          </div>
          <p class="status-text">{{ aiSchedulerStore.generation.currentStep }}</p>
        </div>
        <p v-else class="status-text">Updating proposal...</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useAISchedulerStore } from '../../stores/aiScheduler.js'
import RejectionModal from './RejectionModal.vue'

const props = defineProps({
  proposal: {
    type: Object,
    required: true
  },
  tenant: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['back', 'status-updated'])

const aiSchedulerStore = useAISchedulerStore()

// State
const reviewNotes = ref('')
const updating = ref(false)
const currentWeek = ref(new Date())
const rejectionModal = ref(null)

// Computed properties
const proposalShifts = computed(() => {
  // Backend returns schedule as array of employee schedules
  // Each employee has a shifts array
  
  const employeeSchedules = props.proposal.proposal_data?.shifts || []
  
  // Debug: Log what we received from backend
  console.log('📊 Proposal Data:', {
    total_employees: employeeSchedules.length,
    employees: employeeSchedules.map(e => ({
      id: e.employee_id,
      name: e.employee_name,
      is_dummy: e.is_dummy,
      shift_count: e.shifts?.length || 0
    })),
    proposal_metadata: {
      id: props.proposal.id,
      shift_count: props.proposal.shift_count,
      constraints: props.proposal.constraints,
      target_workers_per_hour: props.proposal.constraints?.target_workers_per_hour
    }
  })
  
  // Flatten to get all shifts across all employees
  const allShifts = []
  employeeSchedules.forEach((empSchedule, idx) => {
    const employeeShifts = empSchedule.shifts || []
    
    employeeShifts.forEach(shift => {
      allShifts.push({
        ...shift,
        id: `${empSchedule.employee_id}-${shift.date}-${shift.start_time}`,
        employee_id: empSchedule.employee_id,
        employee_name: empSchedule.employee_name,
        is_dummy: empSchedule.is_dummy || false
      })
    })
  })
  
  return allShifts
})

const conflicts = computed(() => {
  return props.proposal.proposal_data?.conflicts || []
})

const rulesApplied = computed(() => {
  return props.proposal.proposal_data?.rules_applied || []
})

const uniqueEmployees = computed(() => {
  const employeeIds = new Set(
    proposalShifts.value.map(shift => shift.employee_id).filter(Boolean)
  )
  return employeeIds.size
})

const confidenceClass = computed(() => {
  const score = props.proposal.confidence_score || 0
  if (score >= 0.8) return 'high'
  if (score >= 0.6) return 'medium'
  return 'low'
})

// Calendar computed properties
const weekDays = computed(() => {
  const start = new Date(currentWeek.value)
  const day = start.getDay()
  const diff = start.getDate() - day + (day === 0 ? -6 : 1) // Start on Monday
  start.setDate(diff)
  
  const days = []
  for (let i = 0; i < 7; i++) {
    const date = new Date(start)
    date.setDate(start.getDate() + i)
    days.push(date)
  }
  return days
})

const calendarWeekRange = computed(() => {
  const start = weekDays.value[0]
  const end = weekDays.value[6]
  return `${formatDate(start)} - ${formatDate(end)}`
})

// Methods
const updateStatus = async (status, reason = '') => {
  updating.value = true
  
  try {
    // Special handling for 'reviewing' status - regenerate with feedback
    if (status === 'reviewing') {
      const feedback = reason || reviewNotes.value
      
      if (!feedback || feedback.trim() === '') {
        alert('Please provide specific feedback about what should be improved in the schedule.')
        updating.value = false
        return
      }
      
      // Regenerate proposal with feedback
      const regeneratedProposal = await aiSchedulerStore.requestChanges(
        props.proposal.id,
        feedback
      )
      
      // Emit event with the new proposal
      emit('status-updated', 'regenerated', feedback, regeneratedProposal)
      alert('✨ New proposal generated based on your feedback! Review the updated schedule below.')
      
    } else {
      // Normal status update for approve/reject
      await aiSchedulerStore.updateProposalStatus(
        props.proposal.id,
        status,
        reason || reviewNotes.value
      )
      
      // Record this review for ML training (approved or rejected)
      if (status === 'approved' || status === 'rejected') {
        try {
          await aiSchedulerStore.recordScheduleReview(props.proposal.id)
          console.log(`Review recorded for ML training: ${status}`)
        } catch (error) {
          console.warn('Failed to record review for ML training:', error)
          // Don't fail the whole operation if recording fails
        }
      }
      
      emit('status-updated', status, reason || reviewNotes.value)
      
      // Show success message based on status
      const messages = {
        approved: 'Proposal approved and scheduled for deployment! This schedule will help train the ML model.',
        rejected: 'Proposal rejected with feedback. Your comments will help the ML model learn.',
      }
      
      if (messages[status]) {
        alert(messages[status])
      }
    }
  } catch (error) {
    console.error('Failed to update proposal status:', error)
    alert(`Error: ${error.message}`)
    alert('Failed to update proposal. Please try again.')
  } finally {
    updating.value = false
  }
}

const handleReject = () => {
  rejectionModal.value.show()
}

const handleRejectionConfirmed = (reason) => {
  updateStatus('rejected', reason)
}

const handleRejectionCancelled = () => {
  
}

const getStatusLabel = (status) => {
  const labels = {
    'pending': 'Pending Review',
    'reviewing': 'Under Review',
    'approved': 'Approved',
    'rejected': 'Rejected',
    'committed': 'Deployed'
  }
  return labels[status] || status
}

const getMethodLabel = (method) => {
  const labels = {
    'constraint_solver': 'Constraint Solver',
    'claude': 'Claude AI',
    'ml_model': 'ML Model',
    'rule_engine': 'Rule Engine'
  }
  return labels[method] || method
}

const formatDate = (date) => {
  if (!date) return 'N/A'
  const d = date instanceof Date ? date : new Date(date)
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(d)
}

const formatDateTime = (dateStr) => {
  if (!dateStr) return 'N/A'
  const date = new Date(dateStr)
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric'
  }).format(date)
}

const formatDayHeader = (day) => {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'numeric',
    day: 'numeric'
  }).format(day)
}

const formatRuleName = (rule) => {
  return rule.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
}

const getShiftsForDay = (day) => {
  const dayStr = day.toISOString().split('T')[0]
  const shiftsForDay = proposalShifts.value.filter(shift => shift.date === dayStr)
  
  return shiftsForDay
}

const hasConflict = (shift) => {
  return conflicts.value.some(conflict => 
    conflict.shift_id === shift.id
  )
}

const previousWeek = () => {
  const newWeek = new Date(currentWeek.value)
  newWeek.setDate(newWeek.getDate() - 7)
  currentWeek.value = newWeek
}

const nextWeek = () => {
  const newWeek = new Date(currentWeek.value)
  newWeek.setDate(newWeek.getDate() + 7)
  currentWeek.value = newWeek
}

// Lifecycle
onMounted(() => {
  // Set calendar to show the proposal period
  if (props.proposal.schedule_start_date) {
    currentWeek.value = new Date(props.proposal.schedule_start_date)
  }
})
</script>

<style scoped>
.proposal-reviewer {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  position: relative;
}

.reviewer-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
}

.back-btn {
  padding: 0.5rem 1rem;
  background: #f3f4f6;
  color: #374151;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s;
}

.back-btn:hover {
  background: #e5e7eb;
}

.proposal-info h2 {
  margin: 0;
  color: #1a1a1a;
  font-size: 1.5rem;
  font-weight: 600;
}

.proposal-info p {
  margin: 0.25rem 0 0 0;
  color: #6b7280;
  font-family: monospace;
}

.proposal-summary {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 2rem;
  margin-bottom: 2rem;
}

.summary-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.summary-header h3 {
  margin: 0;
  color: #1a1a1a;
  font-size: 1.25rem;
  font-weight: 600;
}

.confidence-score {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.confidence-label {
  color: #6b7280;
  font-size: 0.875rem;
}

.confidence-value {
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 1.125rem;
}

.confidence-value.high {
  background: #dcfce7;
  color: #166534;
}

.confidence-value.medium {
  background: #fef3c7;
  color: #92400e;
}

.confidence-value.low {
  background: #fef2f2;
  color: #991b1b;
}

.summary-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.stat {
  display: flex;
  justify-content: space-between;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 0.5rem;
}

.stat .label {
  color: #6b7280;
  font-size: 0.875rem;
  font-weight: 500;
}

.stat .value {
  color: #1a1a1a;
  font-weight: 600;
  font-size: 0.875rem;
}

.summary-description {
  border-top: 1px solid #e5e7eb;
  padding-top: 2rem;
}

.summary-description h4 {
  margin: 0 0 1rem 0;
  color: #1a1a1a;
  font-size: 1rem;
  font-weight: 600;
}

.summary-description p {
  margin: 0;
  color: #6b7280;
  line-height: 1.6;
}

.schedule-preview {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 2rem;
  margin-bottom: 2rem;
}

.schedule-preview h3 {
  margin: 0 0 2rem 0;
  color: #1a1a1a;
  font-size: 1.25rem;
  font-weight: 600;
}

.calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.calendar-header h4 {
  margin: 0;
  color: #1a1a1a;
  font-size: 1rem;
  font-weight: 500;
}

.calendar-nav {
  display: flex;
  gap: 0.5rem;
}

.nav-btn {
  width: 32px;
  height: 32px;
  background: #f3f4f6;
  color: #374151;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
}

.nav-btn:hover {
  background: #e5e7eb;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 1px;
  background: #e5e7eb;
  border-radius: 0.5rem;
  overflow: hidden;
}

.day-header {
  background: #f3f4f6;
  padding: 1rem;
  text-align: center;
  font-weight: 600;
  font-size: 0.875rem;
  color: #374151;
}

.day-shifts {
  background: white;
  padding: 0.5rem;
  min-height: 120px;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.shift-block {
  background: #dbeafe;
  border: 1px solid #93c5fd;
  border-radius: 0.375rem;
  padding: 0.5rem;
  font-size: 0.75rem;
  line-height: 1.2;
}

.shift-block.has-conflict {
  background: #fef2f2;
  border-color: #fca5a5;
}

.shift-employee {
  font-weight: 600;
  color: #1e40af;
  margin-bottom: 0.25rem;
}

.shift-block.has-conflict .shift-employee {
  color: #dc2626;
}

.shift-time {
  color: #6b7280;
  font-size: 0.6875rem;
}

.shift-role {
  color: #9ca3af;
  font-size: 0.6875rem;
  font-style: italic;
}

.conflicts-section, .rules-section {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 2rem;
  margin-bottom: 2rem;
}

.conflicts-section h3, .rules-section h3 {
  margin: 0 0 1.5rem 0;
  color: #1a1a1a;
  font-size: 1.25rem;
  font-weight: 600;
}

.conflicts-list, .rules-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.conflict-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 0.5rem;
}

.conflict-icon {
  font-size: 1.25rem;
  flex-shrink: 0;
}

.conflict-type {
  font-weight: 600;
  color: #dc2626;
  margin-bottom: 0.25rem;
}

.conflict-message {
  color: #991b1b;
  font-size: 0.875rem;
}

.rule-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 0.5rem;
}

.rule-icon {
  color: #16a34a;
  font-weight: 600;
}

.rule-name {
  color: #166534;
  font-weight: 500;
}

.review-actions {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 2rem;
  min-height: 200px;
  margin-bottom: 6rem;
}

.review-notes {
  margin-bottom: 2rem;
}

.review-notes label {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: 0.75rem;
  font-weight: 600;
  color: #374151;
}

.review-notes .help-text {
  font-size: 0.75rem;
  font-weight: 400;
  color: #6b7280;
  font-style: italic;
}

.review-notes textarea {
  width: 100%;
  padding: 1rem;
  border: 2px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  line-height: 1.5;
  resize: vertical;
  font-family: inherit;
  transition: border-color 0.2s;
}

.review-notes textarea:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.review-hint {
  display: block;
  margin-top: 0.5rem;
  color: #6b7280;
  font-size: 0.75rem;
  font-style: italic;
}

.action-buttons {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

.btn {
  padding: 0.875rem 2rem;
  border: none;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 160px;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-approve {
  background: #10b981;
  color: white;
}

.btn-approve:hover:not(:disabled) {
  background: #059669;
  transform: translateY(-1px);
}

.btn-changes {
  background: #f59e0b;
  color: white;
}

.btn-changes:hover:not(:disabled) {
  background: #d97706;
  transform: translateY(-1px);
}

.btn-reject {
  background: #ef4444;
  color: white;
}

.btn-reject:hover:not(:disabled) {
  background: #dc2626;
  transform: translateY(-1px);
}

.updating-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.75rem;
  z-index: 1000;
}

.spinner-container {
  padding: 2rem 3rem;
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  text-align: center;
  min-width: 300px;
}

.spinner {
  width: 40px;
  height: 40px;
  margin: 0 auto 1rem;
  border: 4px solid #e5e7eb;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.generation-status {
  margin-top: 1rem;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0.75rem;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6, #8b5cf6);
  transition: width 0.3s ease;
  border-radius: 4px;
}

.status-text {
  font-weight: 600;
  color: #3b82f6;
  margin: 0;
}

@media (max-width: 1024px) {
  .proposal-reviewer {
    padding: 1rem;
  }
  
  .calendar-grid {
    font-size: 0.75rem;
  }
  
  .day-shifts {
    min-height: 100px;
  }
  
  .shift-block {
    padding: 0.375rem;
    font-size: 0.6875rem;
  }
}

@media (max-width: 768px) {
  .reviewer-header {
    flex-direction: column;
    align-items: stretch;
  }
  
  .summary-header {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }
  
  .action-buttons {
    flex-direction: column;
  }
  
  .calendar-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}
</style>